import { useEffect, useState } from "react";
import { useClientAsyncInit } from "@statsig/react-bindings";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import { StatsigTriggeredSessionReplayPlugin } from "@statsig/session-replay";
import {
  setStatsigClient,
  getStatsigClient,
  startStatsigRecording,
  stopStatsigRecording
} from "@/utils/statsig";
import { useUserProfile } from "@/hooks/query/useUserProfile";
import { userAuthorizedSelector } from "@/redux/authorization/authorization.selector";
import { useSelector } from "react-redux";
import { debug } from "@/utils/debug";
import { toStatsigEnvironmentTier } from "@/utils/statsig-environment";
import type { IUser } from "@/types/user.type";

const STATSIG_CLIENT_KEY = String(import.meta.env.VITE_STATSIG_CLIENT_KEY);
const STATSIG_ENVIRONMENT = toStatsigEnvironmentTier(
  import.meta.env.VITE_ENVIRONMENT
);

export const FORCE_RECORDING = false;

const STATSIG_PLUGINS = [
  new StatsigAutoCapturePlugin(),
  new StatsigTriggeredSessionReplayPlugin({
    autoStartRecording: false,
    keepRollingWindow: true
  })
];

export const toStatsigUser = (profile?: IUser) => ({
  userID: profile?.id?.toString() || "",
  email: profile?.email || ""
});

/**
 * Initialises the Statsig SDK and syncs the authenticated user identity.
 *
 * Returns `isReady: true` only after:
 *  1. The SDK has finished loading
 *  2. The user profile has been fetched (if authenticated)
 *  3. `updateUserAsync` has completed so gate checks use the real userID
 *
 * Recording lifecycle is driven by `isAuthorized` (Redux state), so it only
 * reacts to actual auth transitions.
 */
export const useStatsig = ({ allowProfileFetch = true } = {}) => {
  const { isAuthorized } = useSelector(userAuthorizedSelector);
  const { isLoading: isUserLoading, data: user } =
    useUserProfile(allowProfileFetch);

  const { client, isLoading: isSdkLoading } = useClientAsyncInit(
    STATSIG_CLIENT_KEY,
    toStatsigUser(),
    { plugins: STATSIG_PLUGINS, environment: { tier: STATSIG_ENVIRONMENT } }
  );

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    debug.log("[useStatsig] init effect deps changed", {
      isSdkLoading,
      isUserLoading,
      hasUser: !!user,
      hasSingleton: !!getStatsigClient()
    });

    if (isSdkLoading || (allowProfileFetch && isUserLoading)) return;

    let cancelled = false;

    const init = async () => {
      debug.log("[useStatsig] syncing user identity", toStatsigUser(user));
      await client.updateUserAsync(toStatsigUser(user));

      if (cancelled) return;

      if (!getStatsigClient()) {
        debug.log("[useStatsig] registering global statsig client singleton");
        setStatsigClient(client);
      }

      setIsReady(true);
      debug.log("[useStatsig] ready");
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [allowProfileFetch, isSdkLoading, isUserLoading, user, client]);

  useEffect(() => {
    if (isSdkLoading) return;

    if (!isAuthorized) {
      debug.log("[useStatsig] not authorized - clearing identity");
      client.updateUserAsync(toStatsigUser());
    }
  }, [isAuthorized, isSdkLoading, client]);

  useEffect(() => {
    if (isSdkLoading) return;

    if (isAuthorized) {
      if (FORCE_RECORDING) {
        debug.warn(
          "[useStatsig] FORCE_RECORDING is ON - should be used for development only!"
        );
        startStatsigRecording();
      } else {
        debug.log(
          "[useStatsig] isAuthorized=true - starting session recording"
        );
        startStatsigRecording();
      }
    } else {
      debug.log("[useStatsig] isAuthorized=false - stopping session recording");
      stopStatsigRecording();
    }
  }, [isAuthorized, isSdkLoading]);

  return { client, isReady };
};
